import React, { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { PageHeader, Card, Tabs, Modal } from '../components/ui';
import { Download, Upload, Plus, Link as LinkIcon, FileText, Check } from 'lucide-react';

const lastNed = (data, filnavn, mime = 'application/octet-stream') => {
  const blob = new Blob([data], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filnavn; a.click();
  URL.revokeObjectURL(url);
};

const AvtaleGiroOcr = () => {
  const [tab, setTab] = useState('avtalegiro');
  const [avtalegiro, setAvtalegiro] = useState([]);
  const [ocr, setOcr] = useState([]);
  const [skatt, setSkatt] = useState([]);
  const [aar, setAar] = useState(new Date().getFullYear() - 1);
  const [importFil, setImportFil] = useState(null);
  const [koble, setKoble] = useState(null);
  const [medlemmer, setMedlemmer] = useState([]);

  const last = useCallback(() => {
    api.get('/avtalegiro').then((r) => setAvtalegiro(r.data));
    api.get('/ocr').then((r) => setOcr(r.data));
    api.get('/skatt-650').then((r) => setSkatt(r.data));
  }, []);
  useEffect(() => { last(); }, [last]);

  const genererAvtaleGiro = async () => {
    try {
      const r = await api.post('/avtalegiro/generer');
      alert(`Oppdrag ${r.data.id} opprettet med ${r.data.antall_trekk} trekk (${r.data.totalbelop} kr).`);
      last();
    } catch (e) {
      alert('Feil: ' + (e.response?.data?.error || e.message));
    }
  };

  const lastNedAvtaleGiro = async (o) => {
    const r = await api.get(`/avtalegiro/${o.id}/fil`, { responseType: 'blob' });
    lastNed(r.data, `avtalegiro_${o.id}.txt`, 'text/plain');
  };

  const markerSendt = async (o) => {
    await api.post(`/avtalegiro/${o.id}/marker-sendt`);
    last();
  };

  const importerOcr = async () => {
    if (!importFil) return;
    const fd = new FormData();
    fd.append('fil', importFil);
    try {
      const r = await api.post('/ocr/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      alert(`Import: ${r.data.importert} transaksjoner, ${r.data.matchet} auto-matchet.`);
      setImportFil(null);
      last();
    } catch (e) {
      alert('Feil: ' + (e.response?.data?.error || e.message));
    }
  };

  const aapneKoble = async (t) => {
    setKoble(t);
    if (!medlemmer.length) {
      const r = await api.get('/medlemmer', { params: { status: 'aktiv' } });
      setMedlemmer(r.data);
    }
  };

  const kobleTil = async (medlemId) => {
    await api.post(`/ocr/${koble.id}/koble`, { medlem_id: medlemId });
    setKoble(null);
    last();
  };

  const genererSkatt = async () => {
    try {
      const r = await api.post('/skatt-650/generer', { aar });
      alert(`Rapport for ${aar}: ${r.data.antall_givere} givere, ${r.data.total_belop} kr.`);
      last();
    } catch (e) {
      alert('Feil: ' + (e.response?.data?.error || e.message));
    }
  };

  const lastNedSkatt = async (s) => {
    const r = await api.get(`/skatt-650/${s.id}/xml`, { responseType: 'blob' });
    lastNed(r.data, `skatt_650_${s.aar}.xml`, 'application/xml');
  };

  return (
    <>
      <PageHeader
        title="AvtaleGiro · OCR · Skatterapport"
        subtitle="Bankintegrasjon, KID-matching og §6-50-rapport for skattefradrag"
      />
      <Tabs
        value={tab}
        onChange={setTab}
        items={[
          { key: 'avtalegiro', label: 'AvtaleGiro' },
          { key: 'ocr', label: 'OCR-innbetalinger' },
          { key: 'skatt', label: 'Skatterapport §6-50' },
        ]}
        style={{ marginBottom: 12 }}
      />

      {tab === 'avtalegiro' && (
        <Card>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 12 }}>
            <div className="fm-text-muted">BBS OS-formatfil for AvtaleGiro-trekk.</div>
            <button className="btn-primary" onClick={genererAvtaleGiro}><Plus size={14} /> Generer nytt oppdrag</button>
          </div>
          <table className="fm-table">
            <thead><tr><th>ID</th><th>Opprettet</th><th>Trekk-dato</th><th>Antall</th><th>Totalbeløp</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {avtalegiro.map((o) => (
                <tr key={o.id}>
                  <td>#{o.id}</td>
                  <td>{o.opprettet ? new Date(o.opprettet).toLocaleString('nb-NO') : ''}</td>
                  <td>{o.trekk_dato}</td>
                  <td>{o.antall_trekk}</td>
                  <td>{o.totalbelop?.toLocaleString('nb-NO')} kr</td>
                  <td><span className={`badge ${o.status === 'sendt' ? 'badge-success' : 'badge-warning'}`}>{o.status}</span></td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn-ghost" onClick={() => lastNedAvtaleGiro(o)}><Download size={14} /></button>
                    {o.status !== 'sendt' && <button className="btn-ghost" title="Marker sendt" onClick={() => markerSendt(o)}><Check size={14} /></button>}
                  </td>
                </tr>
              ))}
              {avtalegiro.length === 0 && (<tr><td colSpan={7} className="fm-text-muted" style={{ textAlign: 'center', padding: 24 }}>Ingen oppdrag.</td></tr>)}
            </tbody>
          </table>
        </Card>
      )}

      {tab === 'ocr' && (
        <>
          <Card style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="file" accept=".csv,.xml,.txt" onChange={(e) => setImportFil(e.target.files[0])} />
              <button className="btn-primary" disabled={!importFil} onClick={importerOcr}><Upload size={14} /> Importer (CSV/CAMT)</button>
              <span className="fm-text-muted" style={{ fontSize: 12 }}>
                Støtter Nets OCR (.csv) og CAMT.053/054 (.xml). Auto-matcher KID mot fakturaer og AvtaleGiro-medlemmer.
              </span>
            </div>
          </Card>
          <Card>
            <table className="fm-table">
              <thead><tr><th>Dato</th><th>Beløp</th><th>KID</th><th>Avsender</th><th>Status</th><th>Koblet til</th><th></th></tr></thead>
              <tbody>
                {ocr.map((t) => (
                  <tr key={t.id}>
                    <td>{t.dato}</td>
                    <td>{t.belop?.toLocaleString('nb-NO')} kr</td>
                    <td><code>{t.kid}</code></td>
                    <td>{t.avsender_navn || ''}</td>
                    <td><span className={`badge ${t.status === 'matchet' ? 'badge-success' : 'badge-warning'}`}>{t.status}</span></td>
                    <td>{t.koblet_til}</td>
                    <td style={{ textAlign: 'right' }}>
                      {t.status !== 'matchet' && <button className="btn-ghost" onClick={() => aapneKoble(t)}><LinkIcon size={14} /></button>}
                    </td>
                  </tr>
                ))}
                {ocr.length === 0 && (<tr><td colSpan={7} className="fm-text-muted" style={{ textAlign: 'center', padding: 24 }}>Ingen OCR-transaksjoner importert.</td></tr>)}
              </tbody>
            </table>
          </Card>
        </>
      )}

      {tab === 'skatt' && (
        <Card>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 12 }}>
            <label>Inntektsår
              <input type="number" value={aar} onChange={(e) => setAar(Number(e.target.value))} style={{ width: 100, marginLeft: 8 }} />
            </label>
            <button className="btn-primary" onClick={genererSkatt}><FileText size={14} /> Generer §6-50 rapport</button>
            <span className="fm-text-muted" style={{ fontSize: 12 }}>
              Aggregerer skattefradragsgyldige donasjoner og produserer XML for Altinn-innsending.
            </span>
          </div>
          <table className="fm-table">
            <thead><tr><th>År</th><th>Antall givere</th><th>Totalbeløp</th><th>Status</th><th>Generert</th><th></th></tr></thead>
            <tbody>
              {skatt.map((s) => (
                <tr key={s.id}>
                  <td>{s.aar}</td>
                  <td>{s.antall_givere}</td>
                  <td>{s.total_belop?.toLocaleString('nb-NO')} kr</td>
                  <td><span className={`badge ${s.status === 'sendt' ? 'badge-success' : 'badge-warning'}`}>{s.status}</span></td>
                  <td>{s.opprettet ? new Date(s.opprettet).toLocaleDateString('nb-NO') : ''}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button className="btn-ghost" onClick={() => lastNedSkatt(s)}><Download size={14} /></button>
                  </td>
                </tr>
              ))}
              {skatt.length === 0 && (<tr><td colSpan={6} className="fm-text-muted" style={{ textAlign: 'center', padding: 24 }}>Ingen rapporter.</td></tr>)}
            </tbody>
          </table>
        </Card>
      )}

      <Modal open={!!koble} onClose={() => setKoble(null)} title="Koble til medlem" width={520}>
        <div className="fm-text-muted" style={{ marginBottom: 12 }}>
          Velg medlem som transaksjonen skal kobles til:
        </div>
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          {medlemmer.map((m) => (
            <div key={m.id} style={{ padding: 8, borderBottom: '1px solid var(--theme-border)', cursor: 'pointer' }}
                 onClick={() => kobleTil(m.id)}>
              <strong>{m.navn}</strong> {m.medlemsnr && <span className="fm-text-muted">#{m.medlemsnr}</span>}
              <div className="fm-text-muted" style={{ fontSize: 12 }}>{m.epost}</div>
            </div>
          ))}
        </div>
      </Modal>
    </>
  );
};

export default AvtaleGiroOcr;
